// Status mapping constants
export const STATUS_MAP = {
  1: { label: "Open", className: "open", color: "bg-orange-500" },
  2: { label: "Assigned", className: "assigned", color: "bg-blue-500" },
  3: { label: "Resolved", className: "resolved", color: "bg-green-500" },
  4: { label: "Closed", className: "closed", color: "bg-gray-500" },
};

// Priority mapping constants
export const PRIORITY_MAP = {
  1: { label: "Low", className: "low", color: "text-green-600" },
  2: { label: "Medium", className: "medium", color: "text-yellow-600" },
  3: { label: "High", className: "high", color: "text-red-600" },
  4: { label: "Urgent", className: "urgent", color: "text-red-700" },
};

// Get status info from ID
export const getStatusInfo = (statusId) => {
  const parsedStatus = parseInt(statusId);
  return STATUS_MAP[parsedStatus] || { label: "Unknown", className: "unknown", color: "bg-gray-500" };
};

// Get priority info from ID
export const getPriorityInfo = (priorityId) => {
  const parsedPriority = parseInt(priorityId);
  return PRIORITY_MAP[parsedPriority] || { label: "Unknown", className: "unknown", color: "text-gray-600" };
};

// Get ticket number formatted
export const getTicketNumber = (id) => {
  if (!id) return "TKT-0000";
  return `TKT-${String(id).padStart(4, "0")}`;
};

// Format date to DD-MM-YYYY
export const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";

  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "Invalid Date";

  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();

  return `${dd}-${mm}-${yyyy}`;
};

// Format datetime to DD-MM-YYYY HH:MM (local time)
export const formatDateTime = (dateStr) => {
  if (!dateStr) return "N/A";

  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "Invalid Date";

  // Use local time methods to display in user's timezone
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");

  return `${dd}-${mm}-${yyyy} ${hh}:${min}`;
};

// Format datetime with seconds for more precision (local time)
export const formatDateTimeWithSeconds = (dateStr) => {
  if (!dateStr) return "N/A";

  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "Invalid Date";

  // Use local time methods to display in user's timezone
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");

  return `${dd}-${mm}-${yyyy} ${hh}:${min}:${ss}`;
};

// Format datetime with timezone indicator (local time)
export const formatDateTimeWithTimezone = (dateStr) => {
  if (!dateStr) return "N/A";

  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "Invalid Date";

  // Use local time methods to display in user's timezone
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");

  // Get timezone offset and format
  const offset = -d.getTimezoneOffset(); // Invert because getTimezoneOffset returns positive for negative offsets
  const offsetHours = Math.abs(Math.floor(offset / 60));
  const offsetMinutes = Math.abs(offset % 60);
  const offsetSign = offset >= 0 ? '+' : '-';

  return `${dd}-${mm}-${yyyy} ${hh}:${min} (UTC${offsetSign}${String(offsetHours).padStart(2, '0')}:${String(offsetMinutes).padStart(2, '0')})`;
};

// Determine file type by extension
export const getFileType = (fileName) => {
  if (!fileName) return 'other';
  const ext = fileName.split('.').pop().toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) return 'image';
  if (['pdf'].includes(ext)) return 'pdf';
  if (['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv', 'wmv', 'flv'].includes(ext)) return 'video';
  if (['mp3', 'wav', 'ogg', 'aac', 'flac', 'm4a'].includes(ext)) return 'audio';
  return 'other';
};

// Format datetime in local time without timezone indicator (for timeline)
export const formatLocalDateTime = (dateStr) => {
  if (!dateStr) return "N/A";

  // Normalize: DB returns "2024-01-15 10:30:00" (UTC), ensure JS treats it as UTC
  let normalized = dateStr;
  if (typeof normalized === 'string' && !normalized.endsWith('Z') && !normalized.includes('+') && !normalized.includes('T')) {
    normalized = normalized.replace(' ', 'T') + 'Z';
  }

  const d = new Date(normalized);
  if (isNaN(d.getTime())) return "Invalid Date";

  // Use local time methods to display in user's timezone
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");

  return `${dd}-${mm}-${yyyy} ${hh}:${min}`;
};

// Format relative time (e.g., "5 minutes ago")
export const formatRelativeTime = (dateStr) => {
  if (!dateStr) return "N/A";

  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "Invalid Date";
  if (isNaN(date.getTime())) return "Invalid Date";

  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    const seconds = diffInSeconds;
    return `${seconds} second${seconds !== 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} day${days !== 1 ? 's' : ''} ago`;
  } else {
    return formatDateTime(dateStr);
  }
};

// Format file size
export const formatFileSize = (bytes) => {
  if (bytes === null || bytes === undefined || isNaN(bytes)) return "N/A";

  if (bytes < 1024) {
    return `${bytes} bytes`;
  } else if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(2)} KB`;
  } else if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  } else if (bytes < 1024 * 1024 * 1024 * 1024) {
    return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
  } else {
    return `${(bytes / 1024 / 1024 / 1024 / 1024).toFixed(2)} TB`;
  }
};

// Get user initials
export const getInitials = (name) => {
  if (!name) return "U";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

// Safe access nested properties
export const safeGet = (obj, path, defaultValue = null) => {
  const result = path.split('.').reduce((acc, part) => acc?.[part], obj);
  return result !== undefined ? result : defaultValue;
};
